import type { CacheInterface, TypeCacheKey, TypePartialCacheKey } from '@cachehub/core';
import { CacheKeySerializationHelper } from '@cachehub/core';

export class CloudflareCache implements CacheInterface
{
	private readonly getCache: () => Promise<Cache>;

	/**
	 * Constructor
	 *
	 * @param {string} baseUrl - The base URL for cache keys
	 * @param {() => Promise<Cache>} [getCache] - Function to get the cache instance
	 */
	public constructor(private readonly baseUrl: string, getCache?: () => Promise<Cache>)
	{
		this.getCache = getCache ?? (async (): Promise<Cache> => (caches as any).default);
	}

	/**
	 * Sets a value for a given key for a certain period of time
	 *
	 * @param {string | TypeCacheKey} key - The key of the entry
	 * @param {TypeValue} value - The value to be stored
	 * @param {number} [expiresAfter] - The time in seconds after which the entry should expire.
	 * @returns {Promise<boolean>} True if the value is set successfully, false otherwise
	 */
	public async set<TypeValue = string>(key: string | TypeCacheKey, value: TypeValue, expiresAfter?: number): Promise<boolean>
	{
		const cache = await this.getCache();
		const serializedKey = CacheKeySerializationHelper.serialize(key);

		const headers = new Headers();
		if (expiresAfter)
		{
			headers.append('Cache-Control', `max-age=${expiresAfter}`);
		}

		const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
		const response = new Response(serializedValue, { headers });
		await cache.put(this.getCacheKeyAsRequest(serializedKey), response);

		// Track this key for pattern deletion support
		if (typeof key !== 'string')
		{
			await this.trackKey(cache, key, serializedKey);
		}

		return true;
	}

	/**
	 * Returns a value stored for a given key
	 *
	 * @param {string | TypeCacheKey} key - The key of the entry
	 * @returns {Promise<TypeValue | undefined>} The value stored for the given key
	 */
	public async get<TypeValue = string>(key: string | TypeCacheKey): Promise<TypeValue | undefined>
	{
		const cache = await this.getCache();
		const serializedKey = CacheKeySerializationHelper.serialize(key);
		const response = await cache.match(this.getCacheKeyAsRequest(serializedKey));

		if (response && response.status === 200)
		{
			const text = await response.text();
			try
			{
				return JSON.parse(text) as TypeValue;
			}
			catch
			{
				return text as TypeValue;
			}
		}

		return undefined;
	}

	/**
	 * Deletes an entry with a given key or pattern
	 *
	 * Supports:
	 * - String keys: 'exact-key'
	 * - TypeCacheKey objects: All treated as pattern deletion for maximum flexibility
	 * - TypePartialCacheKey objects: Pattern deletion based on provided fields
	 *
	 * @param {string | TypeCacheKey | TypePartialCacheKey} key - The key to delete
	 * @returns {Promise<boolean>} True if any entries were deleted, false otherwise
	 */
	public async delete(key: string | TypeCacheKey | TypePartialCacheKey): Promise<boolean>
	{
		const cache = await this.getCache();

		if (typeof key === 'string')
		{
			// String key - "delete" by setting to null with 1 second TTL
			return await this.deleteByTTL(cache, key);
		}
		else
		{
			// Check if this is a complete TypeCacheKey (has id) vs partial
			if (this.isCompleteKey(key))
			{
				// Complete TypeCacheKey - "delete" by setting to null with TTL
				const serializedKey = CacheKeySerializationHelper.serialize(key as TypeCacheKey);
				const deleted = await this.deleteByTTL(cache, serializedKey);
				if (deleted)
				{
					await this.untrackKey(cache, serializedKey);
				}
				return deleted;
			}
			else
			{
				// TypePartialCacheKey - use pattern deletion
				return await this.deleteByPattern(cache, key as TypePartialCacheKey);
			}
		}
	}

	/**
	 * Checks if a key exists in the cache
	 *
	 * @param {string | CacheKey} key - The key to check
	 * @returns {Promise<boolean>} True if the key exists, false otherwise
	 */
	public async has(key: string | TypeCacheKey): Promise<boolean>
	{
		const cache = await this.getCache();
		const serializedKey = CacheKeySerializationHelper.serialize(key);
		const response = await cache.match(this.getCacheKeyAsRequest(serializedKey));
		return response !== undefined;
	}

	/**
	 * Deletes cache entries matching a pattern using key tracking
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {TypePartialCacheKey} partialKey - The partial key pattern to match
	 * @returns {Promise<boolean>} True if any entries were deleted, false otherwise
	 */
	private async deleteByPattern(cache: Cache, partialKey: TypePartialCacheKey): Promise<boolean>
	{
		// Use the most specific tracking key available for pattern deletion
		const trackingKey = this.getMostSpecificTrackingKey(partialKey);
		let deletedCount = 0;

		const trackedKeys = await this.getTrackedKeys(cache, trackingKey);

		for (const trackedKey of trackedKeys)
		{
			// Double-check that this key actually matches our pattern
			if (this.keyMatchesPattern(trackedKey, partialKey))
			{
				const deleted = await this.deleteByTTL(cache, trackedKey);
				if (deleted)
				{
					deletedCount++;
					// Remove from all tracking lists
					await this.untrackKey(cache, trackedKey);
				}
			}
		}

		return deletedCount > 0;
	}

	/**
	 * Tracks a cache key for pattern deletion support
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {TypeCacheKey} key - The structured cache key
	 * @param {string} serializedKey - The serialized cache key
	 * @returns {Promise<void>}
	 */
	private async trackKey(cache: Cache, key: TypeCacheKey, serializedKey: string): Promise<void>
	{
		const trackingKeys = this.generateTrackingKeys(key);

		for (const trackingKey of trackingKeys)
		{
			try
			{
				const existingKeys = await this.getTrackedKeys(cache, trackingKey);

				if (!existingKeys.includes(serializedKey))
				{
					existingKeys.push(serializedKey);
					await this.setTrackedKeys(cache, trackingKey, existingKeys);
				}
			}
			catch (error)
			{
				console.warn(`Failed to track key ${serializedKey} under ${trackingKey}:`, error);
			}
		}
	}

	/**
	 * Removes a cache key from tracking lists
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} serializedKey - The serialized cache key to untrack
	 * @returns {Promise<void>}
	 */
	private async untrackKey(cache: Cache, serializedKey: string): Promise<void>
	{
		const possiblePatterns = this.generatePossibleTrackingKeys(serializedKey);

		for (const trackingKey of possiblePatterns)
		{
			await this.removeKeyFromTrackingList(cache, trackingKey, serializedKey);
		}
	}

	/**
	 * Removes a key from a specific tracking list
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} trackingKey - The tracking key to remove from
	 * @param {string} serializedKey - The serialized key to remove
	 * @returns {Promise<void>}
	 */
	private async removeKeyFromTrackingList(cache: Cache, trackingKey: string, serializedKey: string): Promise<void>
	{
		try
		{
			const existingKeys = await this.getTrackedKeys(cache, trackingKey);
			const updatedKeys = existingKeys.filter(k => k !== serializedKey);

			if (updatedKeys.length !== existingKeys.length)
			{
				await this.updateOrRemoveTrackingList(cache, trackingKey, updatedKeys);
			}
		}
		catch (error)
		{
			// Ignore errors when cleaning up tracking
		}
	}

	/**
	 * Updates tracking list or removes it if empty
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} trackingKey - The tracking key to update
	 * @param {string[]} updatedKeys - The updated list of keys
	 * @returns {Promise<void>}
	 */
	private async updateOrRemoveTrackingList(cache: Cache, trackingKey: string, updatedKeys: string[]): Promise<void>
	{
		if (updatedKeys.length > 0)
		{
			await this.setTrackedKeys(cache, trackingKey, updatedKeys);
		}
		else
		{
			// Remove empty tracking list by TTL
			await this.deleteByTTL(cache, trackingKey);
		}
	}

	/**
	 * Gets the most appropriate tracking key for a partial key pattern
	 *
	 * @private
	 * @param {TypePartialCacheKey} partialKey - The partial key pattern
	 * @returns {string} The tracking key that contains potential matches
	 */
	private getMostSpecificTrackingKey(partialKey: TypePartialCacheKey): string
	{
		// For patterns with context but no ID, we need to search at namespace level
		// because we track keys hierarchically: namespace -> namespace|id -> namespace|id|context

		if (!partialKey.id && partialKey.context && Object.keys(partialKey.context).length > 0)
		{
			// Pattern like { namespace: 'license', context: { action: 'activation' } }
			// Search at namespace level to find all keys, then filter by context
			return `__track__:${partialKey.namespace}`;
		}

		if (partialKey.id)
		{
			// Pattern like { namespace: 'license', id: 'ABC-123' } or with context
			// Use namespace|id level
			return `__track__:${partialKey.namespace}|${partialKey.id}`;
		}

		// Pattern like { namespace: 'license' } - namespace level
		return `__track__:${partialKey.namespace}`;
	}

	/**
	 * Checks if a serialized key matches a partial key pattern
	 *
	 * @private
	 * @param {string} serializedKey - The serialized cache key
	 * @param {TypePartialCacheKey} partialKey - The partial key pattern to match
	 * @returns {boolean} True if the key matches the pattern
	 */
	private keyMatchesPattern(serializedKey: string, partialKey: TypePartialCacheKey): boolean
	{
		const parts = serializedKey.split('|');

		if (!this.namespaceMatches(parts, partialKey.namespace))
		{
			return false;
		}

		if (!this.idMatches(parts, partialKey.id))
		{
			return false;
		}

		if (!this.contextMatches(parts, partialKey.context))
		{
			return false;
		}

		return true;
	}

	/**
	 * Checks if namespace matches
	 *
	 * @private
	 * @param {string[]} parts - The serialized key parts
	 * @param {string} namespace - The namespace to match
	 * @returns {boolean} True if namespace matches
	 */
	private namespaceMatches(parts: string[], namespace: string): boolean
	{
		return parts.length > 0 && parts[0] === namespace;
	}

	/**
	 * Checks if ID matches (if specified)
	 *
	 * @private
	 * @param {string[]} parts - The serialized key parts
	 * @param {string} [id] - The ID to match (optional)
	 * @returns {boolean} True if ID matches or is not specified
	 */
	private idMatches(parts: string[], id?: string): boolean
	{
		if (!id) return true;
		return parts.length >= 2 && parts[1] === id;
	}

	/**
	 * Checks if context matches (if specified)
	 *
	 * @private
	 * @param {string[]} parts - The serialized key parts
	 * @param {Record<string, any>} [context] - The context to match (optional)
	 * @returns {boolean} True if context matches or is not specified
	 */
	private contextMatches(parts: string[], context?: Record<string, any>): boolean
	{
		if (!context || Object.keys(context).length === 0) return true;

		const contextMap = this.parseContextFromSerializedKey(parts);
		return this.allRequiredContextPropertiesMatch(contextMap, context);
	}

	/**
	 * Parses context from serialized key parts
	 *
	 * @private
	 * @param {string[]} parts - The serialized key parts
	 * @returns {Map<string, string>} Map of context key-value pairs
	 */
	private parseContextFromSerializedKey(parts: string[]): Map<string, string>
	{
		const contextParts = parts.slice(2);
		const contextMap = new Map<string, string>();

		for (const contextPart of contextParts)
		{
			const colonIndex = contextPart.indexOf(':');
			if (colonIndex > 0)
			{
				const key = contextPart.substring(0, colonIndex);
				const value = contextPart.substring(colonIndex + 1);
				const unescapedValue = this.unescapeValue(value);
				contextMap.set(key, unescapedValue);
			}
		}

		return contextMap;
	}

	/**
	 * Checks if all required context properties match
	 *
	 * @private
	 * @param {Map<string, string>} contextMap - Map of parsed context from serialized key
	 * @param {Record<string, any>} requiredContext - Required context properties to match
	 * @returns {boolean} True if all required properties match
	 */
	private allRequiredContextPropertiesMatch(contextMap: Map<string, string>, requiredContext: Record<string, any>): boolean
	{
		for (const [requiredKey, requiredValue] of Object.entries(requiredContext))
		{
			if (requiredValue !== undefined && requiredValue !== null)
			{
				const actualValue = contextMap.get(requiredKey);
				if (actualValue !== requiredValue)
				{
					return false;
				}
			}
		}
		return true;
	}

	/**
	 * Generates tracking keys for a given cache key
	 *
	 * @private
	 * @param {TypeCacheKey | TypePartialCacheKey} key - The cache key
	 * @returns {string[]} Array of tracking keys
	 */
	private generateTrackingKeys(key: TypeCacheKey | TypePartialCacheKey): string[]
	{
		const trackingKeys: string[] = [];

		// Serialize the key to get all parts - always use serializePartial since we removed exact deletion
		const serializedKey = CacheKeySerializationHelper.serializePartial(key);
		const parts = serializedKey.split('|');

		// Generate tracking keys for all levels from 1 to N
		// This allows deletion by any number of parts: namespace, namespace|id, namespace|id|context1, etc.
		for (let i = 1; i <= parts.length; i++)
		{
			const trackingKeyParts = parts.slice(0, i);
			trackingKeys.push(`__track__:${trackingKeyParts.join('|')}`);
		}

		return trackingKeys;
	}

	/**
	 * Generates possible tracking keys for a serialized cache key (for cleanup)
	 *
	 * @private
	 * @param {string} serializedKey - The serialized cache key
	 * @returns {string[]} Array of possible tracking keys
	 */
	private generatePossibleTrackingKeys(serializedKey: string): string[]
	{
		const parts = serializedKey.split('|');
		const trackingKeys: string[] = [];

		// Generate tracking keys for all levels from N down to 1
		// This allows tracking at any depth: namespace, namespace|id, namespace|id|context1, etc.
		for (let i = 1; i <= parts.length; i++)
		{
			const trackingKeyParts = parts.slice(0, i);
			trackingKeys.push(`__track__:${trackingKeyParts.join('|')}`);
		}

		return trackingKeys;
	}

	/**
	 * Gets tracked keys for a tracking key
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} trackingKey - The tracking key
	 * @returns {Promise<string[]>} Array of tracked keys
	 */
	private async getTrackedKeys(cache: Cache, trackingKey: string): Promise<string[]>
	{
		const response = await cache.match(this.getCacheKeyAsRequest(trackingKey));
		if (response && response.status === 200)
		{
			const text = await response.text();
			try
			{
				return JSON.parse(text) as string[];
			}
			catch
			{
				return [];
			}
		}
		return [];
	}

	/**
	 * Sets tracked keys for a tracking key
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} trackingKey - The tracking key
	 * @param {string[]} keys - Array of keys to track
	 * @returns {Promise<void>}
	 */
	private async setTrackedKeys(cache: Cache, trackingKey: string, keys: string[]): Promise<void>
	{
		const headers = new Headers();
		headers.set('Content-Type', 'application/json');
		const response = new Response(JSON.stringify(keys), { headers });
		await cache.put(this.getCacheKeyAsRequest(trackingKey), response);
	}


	/**
	 * Deletes a cache entry by setting it to null with a 1 second TTL
	 * This works around Cloudflare Cache API delete() not working in development
	 *
	 * @private
	 * @param {Cache} cache - The cache instance
	 * @param {string} key - The cache key to delete
	 * @returns {Promise<boolean>} Always returns true since TTL set operations work
	 */
	private async deleteByTTL(cache: Cache, key: string): Promise<boolean>
	{
		try
		{
			// Set to null with 1 second TTL - effectively deletes the entry
			const headers = new Headers();
			headers.append('Cache-Control', 'max-age=1');
			const response = new Response('null', { headers });
			await cache.put(this.getCacheKeyAsRequest(key), response);
			return true;
		}
		catch (error)
		{
			console.warn(`Failed to delete key ${key} using TTL:`, error);
			return false;
		}
	}

	/**
	 * Checks if a key object is a complete TypeCacheKey (has required id field)
	 *
	 * @private
	 * @param {TypeCacheKey | TypePartialCacheKey} key - The key to check
	 * @returns {boolean} True if key has id field (complete), false if partial
	 */
	private isCompleteKey(key: TypeCacheKey | TypePartialCacheKey): key is TypeCacheKey
	{
		return 'id' in key && typeof key.id === 'string' && key.id.length > 0;
	}

	/**
	 * Unescapes special characters in cache key values
	 *
	 * @private
	 * @param {string} value - Value to unescape
	 * @returns {string} Unescaped value
	 */
	private unescapeValue(value: string): string
	{
		// Reverse the escaping done in escapeValue
		return value
			.replace(/\\\*/g, '*')    // Unescape wildcards
			.replace(/\\\|/g, '|')    // Unescape pipe separators
			.replace(/\\\\/g, '\\');  // Unescape backslashes last
	}

	/**
	 * Returns a cache key as request for a given key
	 *
	 * @param {string} key - The key of the entry
	 * @returns {Request} The cache request
	 */
	private getCacheKeyAsRequest(key: string): Request
	{
		const cacheUrl = new URL(`${this.baseUrl}/${key}`);
		return new Request(cacheUrl.toString(), { method: 'GET' });
	}
}
